import { clamp, round0 } from './formatters'

export type PackageType = '' | 'edge' | 'advantage' | 'summit'
export type BuildPayType = '' | 'upfront' | 'two'
export type RevenueCadence = 'one-time' | 'monthly' | 'quarterly'
export type IndustryType =
  | ''
  | 'Windows'
  | 'Roofing'
  | 'Solar (Sales)'
  | 'Solar (Vertical)'
  | 'Solar (Community)'
  | 'Pest'
  | 'Fiber'
  | 'Deregulated Energy'
  | 'Hardscaping'
  | 'Landscaping'
  | 'Other'

export interface Inputs {
  companyName: string
  pkg: PackageType
  buildPay: BuildPayType
  industry: IndustryType
  campaigns: number
  monthlyCampaigns?: number[] // Array of 12 numbers for monthly overrides
  adSpend: number
  monthlyAdSpend?: number[] // Array of 12 numbers for monthly ad spend overrides
  cph: number
  ticket: number
  gpm: number // 0-100 in state, converted to decimal for calc
  salesVetMonthly: number
  salesFirst90Total: number
  pctLaunched: number // 0-100
  pct30: number // 0-100
  pct90: number // 0-100 Day 30 to Day 90 retention
  pct1y: number // 0-100
  pullthrough: number // 0-100
  revenueCadence: RevenueCadence // New: one-time, monthly, or quarterly
  residualTicket?: number // New: monthly ticket for residual products
  monthlyAttrition?: number // New: monthly customer attrition (0-100)
  quarterlyAttrition?: number // New: quarterly customer attrition (0-100)
}

export interface IndustryDefaults {
  ticket: number
  gpm: number
  salesFirst90Total: number
  salesVetMonthly: number
  revenueCadence: RevenueCadence
  residualTicket?: number
}

export const INDUSTRY_DEFAULTS: Record<IndustryType, IndustryDefaults> = {
  Windows: {
    ticket: 20000,
    gpm: 30,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  Roofing: {
    ticket: 20000,
    gpm: 20,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  'Solar (Sales)': {
    ticket: 40000,
    gpm: 10,
    salesFirst90Total: 6,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  'Solar (Vertical)': {
    ticket: 40000,
    gpm: 20,
    salesFirst90Total: 6,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  'Solar (Community)': {
    ticket: 700, // 2x the monthly residual
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 350,
  },
  Pest: {
    ticket: 170, // 2x the monthly residual
    gpm: 40,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 85,
  },
  Fiber: {
    ticket: 100, // 2x the monthly residual
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 50,
  },
  'Deregulated Energy': {
    ticket: 70, // 2x the monthly residual
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 35,
  },
  Hardscaping: {
    ticket: 20000,
    gpm: 25,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  Landscaping: {
    ticket: 50000,
    gpm: 30,
    salesFirst90Total: 5,
    salesVetMonthly: 8,
    revenueCadence: 'one-time',
  },
  Other: {
    ticket: 25000,
    gpm: 20,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
}

export interface PackagePreset {
  name: string
  buildTwoPayments: number
  buildUpfront: number
  systemMonthly: number
  campaignMonthly: number
  bonusLaunch: number
  bonusDay30: number
  bonusDay60: number
  bonusDay90: number
}

export const PACKAGE_PRESETS: Record<PackageType, PackagePreset> = {
  '': {
    name: 'No Package Selected',
    buildTwoPayments: 0,
    buildUpfront: 0,
    systemMonthly: 0,
    campaignMonthly: 0,
    bonusLaunch: 0,
    bonusDay30: 0,
    bonusDay60: 0,
    bonusDay90: 0,
  },
  edge: {
    name: 'HIRE Edge',
    buildTwoPayments: 3000,
    buildUpfront: 3000,
    systemMonthly: 1500,
    campaignMonthly: 1500,
    bonusLaunch: 0,
    bonusDay30: 0,
    bonusDay60: 0,
    bonusDay90: 0,
  },
  advantage: {
    name: 'HIRE Advantage',
    buildTwoPayments: 3000,
    buildUpfront: 5000,
    systemMonthly: 2500,
    campaignMonthly: 2500,
    bonusLaunch: 250,
    bonusDay30: 250,
    bonusDay60: 250,
    bonusDay90: 250,
  },
  summit: {
    name: 'HIRE Summit',
    buildTwoPayments: 3000,
    buildUpfront: 5000,
    systemMonthly: 2500,
    campaignMonthly: 5500,
    bonusLaunch: 250,
    bonusDay30: 250,
    bonusDay60: 250,
    bonusDay90: 250,
  },
}

export interface RowData {
  period: string
  activeCampaigns: number
  hires: number
  launched: number
  active30d: number
  active60d: number
  active90d: number
  veterans: number
  buildFee: number
  platformFee: number
  campaignFee: number
  hireCost: number
  adSpend: number
  bonuses: number
  totalCost: number
  totalSales: number
  realizationRate: number
  grossRevenue: number
  grossProfit: number
  netProfit: number
  cumProfit: number
  residualRevenue?: number // New: monthly residual revenue
  totalResidualRevenue?: number // New: total revenue including residuals
}

export interface CalculationResult {
  preset: PackagePreset
  hiresPerMonth: number
  launchedPerMonth: number
  gpPerSale: number
  totalHireCost: number
  totalAdSpend: number
  totalBonuses: number
  totalCostAll: number
  totalGrossProfit: number
  totalNetProfit: number
  roi: number
  breakeven: string
  rows: RowData[]
}

export const DEFAULT_INPUTS: Inputs = {
  companyName: '',
  pkg: '',
  buildPay: 'upfront',
  industry: '',
  campaigns: 1,
  monthlyCampaigns: Array(12).fill(1),
  adSpend: 5000,
  monthlyAdSpend: Array(12).fill(5000),
  cph: 350,
  ticket: 20000,
  gpm: 30,
  salesVetMonthly: 6,
  salesFirst90Total: 10,
  pctLaunched: 70,
  pct30: 30,
  pct90: 80,
  pct1y: 30,
  pullthrough: 80,
  revenueCadence: 'one-time',
  residualTicket: undefined,
  monthlyAttrition: 2, // Default 2% monthly attrition
  quarterlyAttrition: 5, // Default 5% quarterly attrition
}

export function calculateModel(inputs: Inputs): CalculationResult {
  const months = 12
  const preset = PACKAGE_PRESETS[inputs.pkg]

  // Convert percentages from 0-100 inputs to 0-1 decimals
  const gpm = clamp(inputs.gpm / 100, 0, 1)
  const pctLaunched = clamp(inputs.pctLaunched / 100, 0, 1)
  const pct30 = clamp(inputs.pct30 / 100, 0, 1)
  const pct90 = clamp(inputs.pct90 / 100, 0, 1)
  const pct1y = clamp(inputs.pct1y / 100, 0, 1)
  const pullthrough = clamp(inputs.pullthrough / 100, 0, 1)

  // Calculate pct60 as square root of pct90 (so pct60 * pct60 = pct90)
  // This splits the 30-90 day attrition evenly across the two 30-day periods
  const pct60 = Math.sqrt(pct90)

  // Use monthly campaigns if available, otherwise default to single value
  const monthlyCampaigns =
    inputs.monthlyCampaigns && inputs.monthlyCampaigns.length === 12
      ? inputs.monthlyCampaigns
      : Array(12).fill(Math.max(1, inputs.campaigns))

  // Use monthly ad spend if available, otherwise default to single value
  const monthlyAdSpendValues =
    inputs.monthlyAdSpend && inputs.monthlyAdSpend.length === 12
      ? inputs.monthlyAdSpend
      : Array(12).fill(Math.max(0, inputs.adSpend))

  // Calculate monthly metrics based on varying campaign counts
  const cph = Math.max(1, inputs.cph)

  // Monthly Hires & Launched (varies by month based on campaigns and ad spend)
  const hired = monthlyCampaigns.map((campaigns, idx) => {
    const adSpendPerCampaign = monthlyAdSpendValues[idx]
    const totalAdSpend = adSpendPerCampaign * campaigns
    return round0(totalAdSpend / cph)
  })

  const launched = hired.map((h) => h * pctLaunched)

  // Funnel per cohort (monthly cohorts)
  // Each cohort goes through: hired → launched → day30 → day60 → day90
  const cohortDay30 = launched.map((x) => x * pct30)

  // Day 60: Apply Day 30 to Day 60 retention rate (calculated as sqrt of pct90)
  const cohortDay60 = cohortDay30.map((x) => x * pct60)

  // Day 90: Apply Day 60 to Day 90 retention rate (also sqrt of pct90)
  const cohortDay90 = cohortDay60.map((x) => x * pct60)

  // Active counts by month
  // Month 1 hires → active at 30 days in Month 2
  const active30d = Array.from({ length: months }, (_, m) => {
    return m >= 1 ? cohortDay30[m - 1] : 0
  })

  // Month 1 hires → active at 60 days in Month 3
  const active60d = Array.from({ length: months }, (_, m) => {
    return m >= 2 ? cohortDay60[m - 2] : 0
  })

  // Month 1 hires → active at 90 days in Month 4
  const active90d = Array.from({ length: months }, (_, m) => {
    return m >= 3 ? cohortDay90[m - 3] : 0
  })

  // Veterans: reps who have been active for 120+ days
  // Monthly attrition from Day 90 to 1 Year: use pct1y input
  // pct1y represents retention over 9 months (90 days to 1 year)
  // Monthly retention rate = pct1y ^ (1/9)
  const monthlyVeteranRetentionRate = Math.pow(pct1y, 1 / 9)

  const veterans: number[] = []
  for (let m = 0; m < months; m++) {
    if (m <= 3) {
      // No veterans until Month 5 (index 4)
      // Month 4 (index 3) has first 90d cohort, but they're not veterans yet
      veterans.push(0)
    } else {
      // Month 5+ (index 4+): Apply attrition to previous veterans + add new graduates
      const previousVeterans = veterans[m - 1] * monthlyVeteranRetentionRate

      // New graduates: cohort from 4 months ago (now at 120 days)
      // In Month 5 (m=4), we add cohort from Month 1 (m=0) which is now 120 days old
      const newGraduates = m >= 4 ? cohortDay90[m - 4] : 0

      veterans.push(previousVeterans + newGraduates)
    }
  }

  // NEW SALES LOGIC: Sales are based on reps who will eventually reach 90 days
  // The total sales (salesFirst90Total) are split evenly across the 3 months
  // Each cohort's eventual 90-day graduates contribute 1/3 of their total sales each month
  const salesPer30Days = Math.max(0, inputs.salesFirst90Total) / 3

  // Calculate sales for each month based on cohorts that will eventually reach 90 days
  // SHIFTED: Reps hired in a month start making sales in that same month
  const totalSales = Array.from({ length: months }, (_, m) => {
    let sales = 0

    // Sales from cohorts in their first 30 days (will eventually be cohortDay90)
    // Month 1: Month 1 cohort in days 1-30 contributes 1/3 of eventual 90-day sales
    if (m >= 0) {
      const eventualGrads = cohortDay90[m]
      sales += eventualGrads * salesPer30Days
    }

    // Sales from cohorts in their second 30 days (days 31-60)
    // Month 2: Month 1 cohort in days 31-60 contributes another 1/3
    if (m >= 1) {
      const eventualGrads = cohortDay90[m - 1]
      sales += eventualGrads * salesPer30Days
    }

    // Sales from cohorts in their third 30 days (days 61-90)
    // Month 3: Month 1 cohort in days 61-90 contributes final 1/3
    if (m >= 2) {
      const eventualGrads = cohortDay90[m - 2]
      sales += eventualGrads * salesPer30Days
    }

    // Sales from veterans (reps past 90 days)
    // In Month 4+, reps who reached 90 days start producing at veteran rate
    // Month 4: Month 1 cohort is now at 90+ days (veteran)
    // Month 5: Month 1 cohort (120+ days) + Month 2 cohort (90-120 days)
    if (m >= 3) {
      // All cohorts that have reached 90+ days
      for (let cohortMonth = 0; cohortMonth <= m - 3; cohortMonth++) {
        // Calculate veteran count for this cohort
        let veteranCount = cohortDay90[cohortMonth]

        // Apply monthly attrition for each month past 90 days
        const monthsPast90 = m - cohortMonth - 3
        for (let ageMonth = 0; ageMonth < monthsPast90; ageMonth++) {
          veteranCount *= monthlyVeteranRetentionRate
        }

        sales += veteranCount * Math.max(0, inputs.salesVetMonthly)
      }
    }

    // Return total sales BEFORE pullthrough
    return sales
  })

  // Calculate realization (pullthrough applied to total sales)
  const realizedSales = totalSales.map((x) => x * pullthrough)

  // NEW: Calculate residual revenue for monthly/quarterly cadence
  const residualRevenue: number[] = []
  const totalResidualRevenue: number[] = []

  if (inputs.revenueCadence === 'monthly' && inputs.residualTicket) {
    // Monthly residual: based on NEW monthly revenue from previous months with 3-month lag
    // Month 4 residual = Month 1's NEW monthly revenue
    // Month 5 residual = Month 1's + Month 2's NEW monthly revenue (with retention)
    // Month 6 residual = Month 1's + Month 2's + Month 3's NEW monthly revenue (with retention)
    // Month 7 residual = Month 1's + Month 2's + Month 3's + Month 4's NEW monthly revenue (with retention)
    // And so on...

    const monthlyAttritionRate = clamp(
      (inputs.monthlyAttrition || 2) / 100,
      0,
      1,
    )
    const customerRetention = 1 - monthlyAttritionRate

    // Calculate NEW monthly revenue for each month (initial sales revenue)
    const newMonthlyRevenue = realizedSales.map(
      (sales) => sales * Math.max(0, inputs.ticket),
    )

    for (let m = 0; m < months; m++) {
      let monthlyResidual = 0

      // Sum up residual revenue from all months that are 3+ months old
      // Month 4 (index 3): gets residual from Month 1 (index 0)
      // Month 5 (index 4): gets residual from Months 1 and 2
      // Month 6 (index 5): gets residual from Months 1, 2, and 3
      // Month 7 (index 6): gets residual from Months 1, 2, 3, and 4
      // Month 8 (index 7): gets residual from Months 1, 2, 3, 4, and 5
      // And so on...

      if (m >= 3) {
        // For each month that's at least 3 months old
        for (let sourceMonth = 0; sourceMonth <= m - 3; sourceMonth++) {
          // Calculate how many months have passed since this revenue started generating residuals
          const monthsGeneratingResidual = m - sourceMonth - 3

          // Apply monthly retention for each month
          const retentionFactor = Math.pow(
            customerRetention,
            monthsGeneratingResidual,
          )

          // Add this month's contribution to residual revenue
          // Convert NEW monthly revenue to customer count, apply retention, multiply by residual ticket
          monthlyResidual +=
            (newMonthlyRevenue[sourceMonth] / Math.max(0, inputs.ticket)) *
            retentionFactor *
            inputs.residualTicket
        }
      }

      residualRevenue.push(monthlyResidual)

      // Total revenue = initial sale + residual
      totalResidualRevenue.push(
        realizedSales[m] * Math.max(0, inputs.ticket) + monthlyResidual,
      )
    }
  } else if (inputs.revenueCadence === 'quarterly' && inputs.residualTicket) {
    // Quarterly residual: rolling 3-month billing cycle
    // Each month bills customers from exactly 3 months prior (with quarterly attrition)
    // Month 4 bills Month 1's customers
    // Month 5 bills Month 2's customers
    // Month 6 bills Month 3's customers
    // Month 7 bills Month 4's customers
    // And so on...

    const quarterlyAttritionRate = clamp(
      (inputs.quarterlyAttrition || 5) / 100,
      0,
      1,
    )
    const quarterlyRetention = 1 - quarterlyAttritionRate

    // Calculate NEW monthly revenue for each month (initial sales revenue)
    const newMonthlyRevenue = realizedSales.map(
      (sales) => sales * Math.max(0, inputs.ticket),
    )

    for (let m = 0; m < months; m++) {
      let quarterlyResidual = 0

      // Starting from Month 4 (index 3), bill customers from 3 months prior
      if (m >= 3) {
        const sourceMonth = m - 3
        // Convert NEW monthly revenue to customer count, apply quarterly retention, multiply by quarterly ticket
        quarterlyResidual =
          (newMonthlyRevenue[sourceMonth] / Math.max(0, inputs.ticket)) *
          quarterlyRetention *
          inputs.residualTicket
      }

      residualRevenue.push(quarterlyResidual)

      // Total revenue = initial sale + residual
      totalResidualRevenue.push(
        realizedSales[m] * Math.max(0, inputs.ticket) + quarterlyResidual,
      )
    }
  } else {
    // One-time revenue (existing behavior)
    for (let m = 0; m < months; m++) {
      residualRevenue.push(0)
      totalResidualRevenue.push(realizedSales[m] * Math.max(0, inputs.ticket))
    }
  }

  // Calculate gross revenue using total residual revenue
  const grossRevenue = totalResidualRevenue

  // Calculate gross profit (gross revenue × margin)
  const grossProfit = grossRevenue.map((x) => x * gpm)

  // Calculate gross profit per sale for summary stats
  const gpPerSale = Math.max(0, inputs.ticket) * gpm

  // Costs
  const buildLaunch =
    inputs.buildPay === 'upfront'
      ? preset.buildUpfront
      : (preset.buildUpfront + 1000) / 2 // (Upfront value + $1000) / 2 for Build payment
  const buildM1 = 0 // No payment in Month 1
  const buildM2 =
    inputs.buildPay === 'upfront' ? 0 : (preset.buildUpfront + 1000) / 2 // (Upfront value + $1000) / 2 for Month 2 payment

  const systemMonthly = preset.systemMonthly

  // Campaign monthly fee depends on number of campaigns per month
  const campaignFees = monthlyCampaigns.map((c) => preset.campaignMonthly * c)

  // Bonuses - ACCURATE calculation based on actual cohort progression
  // All bonuses are billed the MONTH AFTER the milestone is hit

  // Launch bonus: paid the month AFTER launch
  const bonusLaunch = Array(months)
    .fill(0)
    .map((_, m) => {
      const cohortIdx = m - 1
      return cohortIdx >= 0 ? preset.bonusLaunch * launched[cohortIdx] : 0
    })

  // Day 30 bonus: paid when cohort reaches 30 days (2 months after launch)
  const bonusDay30 = Array(months)
    .fill(0)
    .map((_, m) => {
      const cohortIdx = m - 2
      return cohortIdx >= 0 ? preset.bonusDay30 * cohortDay30[cohortIdx] : 0
    })

  // Day 60 bonus: paid when cohort reaches 60 days (3 months after launch)
  const bonusDay60 = Array(months)
    .fill(0)
    .map((_, m) => {
      const cohortIdx = m - 3
      return cohortIdx >= 0 ? preset.bonusDay60 * cohortDay60[cohortIdx] : 0
    })

  // Day 90 bonus: paid when cohort reaches 90 days (4 months after launch)
  const bonusDay90 = Array(months)
    .fill(0)
    .map((_, m) => {
      const cohortIdx = m - 4
      return cohortIdx >= 0 ? preset.bonusDay90 * cohortDay90[cohortIdx] : 0
    })

  const bonuses = bonusLaunch.map(
    (x, i) => x + bonusDay30[i] + bonusDay60[i] + bonusDay90[i],
  )

  // Monthly HIRE cost - now separated into components
  const buildFees = Array(months)
    .fill(0)
    .map((_, i) => {
      if (i === 0) return buildM1 // Month 1: no payment
      if (i === 1) return buildM2 // Month 2: second half (if split)
      return 0
    })

  const platformFees = Array(months).fill(systemMonthly)

  const hireCost = Array(months)
    .fill(0)
    .map((_, i) => buildFees[i] + platformFees[i] + campaignFees[i])

  const adSpend = monthlyCampaigns.map(
    (c, idx) => monthlyAdSpendValues[idx] * c,
  )

  const totalCost = Array(months)
    .fill(0)
    .map((_, i) => hireCost[i] + adSpend[i] + bonuses[i])
  const netProfit = grossProfit.map((x, i) => x - totalCost[i])

  // Rows
  const rows: RowData[] = []
  let cum = -buildLaunch

  rows.push({
    period: 'Build',
    activeCampaigns: 0,
    hires: 0,
    launched: 0,
    active30d: 0,
    active60d: 0,
    active90d: 0,
    veterans: 0,
    buildFee: buildLaunch,
    platformFee: 0,
    campaignFee: 0,
    hireCost: buildLaunch,
    adSpend: 0,
    bonuses: 0,
    totalCost: buildLaunch,
    totalSales: 0,
    realizationRate: pullthrough,
    grossRevenue: 0,
    grossProfit: 0,
    netProfit: -buildLaunch,
    cumProfit: cum,
    residualRevenue: 0,
    totalResidualRevenue: 0,
  })

  for (let i = 0; i < months; i++) {
    cum += netProfit[i]
    rows.push({
      period: `Month ${i + 1}`,
      activeCampaigns: monthlyCampaigns[i],
      hires: hired[i],
      launched: launched[i],
      active30d: active30d[i],
      active60d: active60d[i],
      active90d: active90d[i],
      veterans: veterans[i],
      buildFee: buildFees[i],
      platformFee: platformFees[i],
      campaignFee: campaignFees[i],
      hireCost: hireCost[i],
      adSpend: adSpend[i],
      bonuses: bonuses[i],
      totalCost: totalCost[i],
      totalSales: totalSales[i],
      realizationRate: pullthrough,
      grossRevenue: grossRevenue[i],
      grossProfit: grossProfit[i],
      netProfit: netProfit[i],
      cumProfit: cum,
      residualRevenue: residualRevenue[i],
      totalResidualRevenue: totalResidualRevenue[i],
    })
  }

  const totalHireCost = buildLaunch + hireCost.reduce((a, b) => a + b, 0)
  const totalAdSpend = adSpend.reduce((a, b) => a + b, 0)
  const totalBonuses = bonuses.reduce((a, b) => a + b, 0)
  const totalCostAll = totalHireCost + totalAdSpend + totalBonuses
  const totalGrossProfit = grossProfit.reduce((a, b) => a + b, 0)
  const totalNetProfit =
    totalGrossProfit -
    (hireCost.reduce((a, b) => a + b, 0) + totalAdSpend + totalBonuses) -
    buildLaunch
  const roi = totalCostAll > 0 ? (totalNetProfit / totalCostAll) * 100 : 0

  let breakeven = 'Not within 12 months'
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].cumProfit >= 0) {
      breakeven = rows[i].period
      break
    }
  }

  return {
    preset,
    hiresPerMonth: hired[0], // Just use month 1 for summary
    launchedPerMonth: launched[0], // Just use month 1 for summary
    gpPerSale,
    totalHireCost,
    totalAdSpend,
    totalBonuses,
    totalCostAll,
    totalGrossProfit,
    totalNetProfit,
    roi,
    breakeven,
    rows,
  }
}
